import { throttle, debounce } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { formatDistanceStrict, formatDistanceToNow } from 'date-fns'
import { Modal, ModalBody, ModalHeader, SomethingWentWrongSVG, WarningIcon, useModal } from '@panwds/react-ui'
import { setAuthValues, setValuesInternal } from '../../framework/actions'
import events from '../../framework/events'
import { stateRef } from '../../framework/store'
import { setTimeout, clearTimeout, setInterval, clearInterval } from '../../framework/utils'
import { __info } from '../../init/debug'
import type { MutableRefObject, VFC } from 'react'

const DEFAULT_IDLE_TIMEOUT = 1800000 // 30min
const LAST_ACTIVITY_KEY = 'sparky-last-activity'

const DOCUMENT_EVENTS = [
  'mousemove', 'mousedown', 'wheel', 'click', // mouse events
  'keydown', 'keypress', // keyboard events
  // 'touchmove', 'touchstart', 'touchend', // touch events
]

export const inactivityLogout = (msg: string) => {
  if (stateRef.current.loggingOut) {
    __info('already logging out')
    return
  }
  // const msg = _T('You were idle for more than 30 minutes so we logged you out for your safety.')
  if (stateRef.current.oidc?.userManager) {
    Object.defineProperties(stateRef.current.oidc.userManager, {
      inactivityLogout: { value: true },
      inactivityLogoutMessage: { value: msg },
    })
  }
  events.emit('logout')
  setValuesInternal({
    loggingOut: 'silent',
  })
  // cleanup auth
  setAuthValues({
    access: undefined,
    access_token: undefined,
    access_token_decoded: undefined,
    fawkes_tenant_id: undefined,
    id_token: undefined,
    user: undefined,
    tsg_id: undefined,
    tsg_name: undefined,
    logout_reason: 'inactivity',
  })
}

// TODO: handle auto logout if already lost session

const win = window
type SyncHandler = (value: number) => void
type HandleSyncFn = (handler: SyncHandler) => [update: (value: number) => void, stop: () => void]
const handleSync: HandleSyncFn = 'BroadcastChannel' in window && !('polyfilled' in BroadcastChannel) ? (handler) => {
  const channel = new BroadcastChannel(LAST_ACTIVITY_KEY)
  channel.addEventListener('message', (event) => {
    handler(Number(event.data))
  }, { passive: true })
  const stop = () => {
    channel.close()
  }
  const debouced = debounce(updateStorage, 1000)
  const update = (value: number) => {
    // console.info('do sync')
    channel.postMessage(value)
    debouced(value)
  }
  return [update, stop]
} : (handler) => {
  const storageHandler = (event: StorageEvent) => {
    if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
      handler(Number(event.newValue))
    }
  }
  win.addEventListener('storage', storageHandler, { passive: true })
  const stop = () => {
    win.removeEventListener('storage', storageHandler)
  }
  return [debounce(updateStorage, 500), stop]
}
const updateStorage = (value: number) => {
  // console.info('save localStorage')
  localStorage.setItem(LAST_ACTIVITY_KEY, String(value))
}
const handleEvents = (handler: () => void) => {
  for (const event of DOCUMENT_EVENTS) {
    win.document.addEventListener(event, handler, { passive: true })
  }
  win.addEventListener('scroll', handler, { capture: true, passive: true })
  win.addEventListener('visibilitychange', handler, { passive: true })

  return () => {
    for (const event of DOCUMENT_EVENTS) {
      win.document.removeEventListener(event, handler)
    }
    win.removeEventListener('scroll', handler, { capture: true })
    win.removeEventListener('visibilitychange', handler)
  }
}
const listenInactivity = (handler: SyncHandler) => {
  const throttledHandler = throttle(handler, 500)
  const stopListen = handleEvents(throttle(() => {
    const now = Date.now()
    // console.info('update latest activity', now)
    throttledHandler(now)
    sync(now)
  }, 1000))
  const [sync, stopSync] = handleSync((value) => {
    // console.info('update latest activity via sync', value)
    throttledHandler(value)
  })
  return () => {
    stopListen()
    stopSync()
  }
}

type TimeoutOptions = {
  timeout: number
  warningTimeout: number
  onTimeout: () => void
  onWarning: (value: number) => void
  onActive: (value: number) => void
}
export const inactivityTimeout = ({
  timeout,
  warningTimeout,
  onTimeout,
  onWarning,
  onActive,
}: TimeoutOptions) => {
  let lastActivity: number = 0
  let timeoutId: number = 0
  const getLastActivity = () => {
    const stored = Number(localStorage.getItem(LAST_ACTIVITY_KEY))
    if (stored && stored > lastActivity) {
      lastActivity = stored
    }
    return lastActivity
  }
  const updateActivity = (value: number) => {
    lastActivity = value
    if (timeoutId) clearTimeout(timeoutId)
    const timeoutFn = () => {
      const latest = getLastActivity()
      const diff = latest > 0 ? latest - Date.now() : 0
      if (diff > 500) {
        timeoutId = setTimeout(timeoutFn, diff)
      }
      else {
        onWarning(lastActivity + timeout)
        timeoutId = setTimeout(() => {
          onTimeout()
          stop()
        }, warningTimeout)
      }
    }
    timeoutId = setTimeout(timeoutFn, timeout - warningTimeout)
    onActive(lastActivity)
  }
  const stop = listenInactivity(updateActivity)
  updateActivity(Date.now())

  return stop
}

export const InactivityTimeoutWarningModal: VFC<{ isOpen: boolean, countDownRef: MutableRefObject<number> }> = ({
  isOpen, countDownRef,
}) => {
  const [duration, setDuration] = useState<string>('')
  useEffect(() => {
    const cls = 'spf-z-[1000]'
    const appClassList = document.querySelector('.sparky-left-nav')?.classList
    appClassList?.remove(cls)
    let lastDuration = ''
    const updateDuration = () => {
      if (countDownRef.current < Date.now()) {
        clearInterval(timer)
        timer = 0
        return
      }
      const newDuration = formatDistanceToNow(countDownRef.current, { includeSeconds: true })
      if (newDuration !== lastDuration) {
        setDuration(newDuration)
        lastDuration = newDuration
      }
    }
    let timer = setInterval(updateDuration, 1000)
    updateDuration()
    return () => {
      if (timer) {
        clearInterval(timer)
      }
      appClassList?.add(cls)
    }
  }, [countDownRef])
  const icon = <WarningIcon size='sm' className='spf-fill-current spf-text-orange-400 spf-mb-1 spf-mr-1' />
  return <Modal isOpen={isOpen} size='sm'>
    <ModalHeader title={<>{icon} Session Timeout</>} />
    <ModalBody addClassName='spf-text-center spf-py-8'>
      <SomethingWentWrongSVG width={'6rem'} />
      <div className='spf-mt-8'>Your session is about to timeout<br />and you will be logged out in {duration}.</div>
      <small>Any unsaved work will be lost.<br />Press any key or move the cursor to resume your session.</small>
    </ModalBody>
  </Modal>
}

export const useInactivityTimeout = (config: boolean | string | undefined) => {
  const countDownRef = useRef(0)
  const modal = useModal(useMemo(() => {
    const InactivityTimeoutWarningModalComp: VFC<{ isOpen: boolean }> = ({ isOpen }) => <InactivityTimeoutWarningModal isOpen={isOpen} countDownRef={countDownRef} />
    return InactivityTimeoutWarningModalComp
  }, []))
  const modalRef = useRef(modal)
  modalRef.current = modal
  useEffect(() => {
    if (!config) return
    const [timeoutStr, warningTimeoutStr] = config === true ? [DEFAULT_IDLE_TIMEOUT] :
      config ? String(config).split(/\D/, 2) : []
    const timeout = +timeoutStr
    if (!timeout) return
    const warningTimeout = warningTimeoutStr ? +warningTimeoutStr : Math.round(timeout / 6)
    return inactivityTimeout({
      timeout,
      warningTimeout,
      onWarning(value) {
        countDownRef.current = value
        modalRef.current.openModal()
      },
      onTimeout() {
        modalRef.current.closeModal()
        const msg = `You were idle for more than ${formatDistanceStrict(timeout, 0, { unit: 'minute' })} so we logged you out for your safety.`
        inactivityLogout(msg)
      },
      onActive() {
        modalRef.current.closeModal()
      },
    })
  }, [config])
}

export default useInactivityTimeout
